import { Component } from '@angular/core';
import { ViewDidEnter, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { filter, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  BrowseHierarchyFacadeService,
  ProductAnalyticsService,
  LoadingState,
} from '@usf/ngrx-product';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { ProductClassCategories } from '@usf/product-types';
import { environment } from '../../../../../environments/environment';
import { EndecaSearchPagesTypes } from '@shared/constants/endeca-search-pages-types';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { PATHS } from '@shared/constants/paths';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { capitalizeText } from '@search/utils/search-text.util';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { CATEGORY_FACET } from '@app/new-search/constants/new-search-constants';

@Component({
  selector: 'app-browse-sub-categories',
  templateUrl: './browse-sub-categories.page.html',
  styleUrls: ['./browse-sub-categories.page.scss'],
})
export class BrowseSubCategoriesPage
  implements ViewWillEnter, ViewWillLeave, ViewDidEnter
{
  subCategories: ProductClassCategories[];
  browseHierarchyViewModel$: Subscription;
  baseCategoryImageUri = `${environment.mediaServerUrl}/`;
  breadcrumbs: Crumb[];
  classCode: string;
  classDescription: string;

  isNewSearchEnabled = false;
  newSearchSubscription$: Subscription;

  constructor(
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    protected panAppState: PanAppState,
    private browseHierarchyFacadeService: BrowseHierarchyFacadeService,
    private route: ActivatedRoute,
    private navigationHelper: NavigationHelperService,
    private productAnalyticService: ProductAnalyticsService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
  ) {}
  ionViewDidEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.browseProductSubCategoriesPage),
    );
  }
  ionViewWillEnter(): void {
    this.route.paramMap.subscribe(params => {
      this.loadSubCategoriesByClassCode(params.get('classCode'));
    });

    this.newSearchSubscription$ =
      this.toggleNewSearchService.newSearchEnabledObservable$.subscribe(
        enabled => (this.isNewSearchEnabled = enabled),
      );
  }

  loadSubCategoriesByClassCode(classCode: string) {
    this.browseHierarchyFacadeService.loadBrowseCategories();
    this.browseHierarchyViewModel$ = this.browseHierarchyFacadeService
      .getBrowseHierarchyViewModel()
      .pipe(
        filter(vm => vm?.state !== LoadingState.loading),
        take(1),
      )
      .subscribe(value => {
        const categories = value?.categories.filter(
          category => category.classCode === classCode,
        );
        this.classCode = categories[0]?.classCode;
        this.classDescription = categories[0]?.classDescription;
        this.subCategories = [...categories[0]?.categories].sort((a, b) =>
          a.categoryDescription.localeCompare(b.categoryDescription),
        );
        this.capitalizeClassDescriptionText();
        this.loadBreadCrumbsValues();
        this.productAnalyticService.trackCatalogCategoryBrowsePageLoad(
          this.classDescription,
        );
      });
  }

  capitalizeClassDescriptionText() {
    this.classDescription = capitalizeText(this.classDescription);
  }

  loadBreadCrumbsValues() {
    this.breadcrumbs = [
      {
        label: 'i18n.search.browsePage.title',
        url: '/desktop/search/browse',
        active: false,
      },
      {
        label: 'i18n.search.browsePage.category.title',
        url: '/desktop/search/browse/categories',
        active: false,
      },
      {
        label: this.classDescription,
        url: '',
        active: true,
      },
    ];
  }

  viewCategory(code: string, pageType: string, description: string): void {
    const queryParams = {
      searchFilterProperties: `${code}`,
      categoryId: `${code}`,
      pageType: pageType,
      originSearchPage: SEARCH_PAGES_ENUM.catalog,
    };
    if (this.isNewSearchEnabled === false) {
      this.navigationHelper.routeTo(PATHS.SEARCH, queryParams);
    } else {
      const encodedDescription = encodeURIComponent(description);
      this.navigationHelper.routeToNewSearch({
        facetFilters: `${CATEGORY_FACET}:${encodedDescription}`,
      });
    }
  }

  ionViewWillLeave(): void {
    this.browseHierarchyViewModel$.unsubscribe();
    this.newSearchSubscription$?.unsubscribe();
  }

  public get EndecaSearchPagesTypes() {
    return EndecaSearchPagesTypes;
  }
}
